import { createClient } from '@supabase/supabase-js';
import { sanitizeFilename } from '../utils/utils';

// Initialize the Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'your-supabase-url';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-supabase-anon-key';
// Add persistent sessions configuration
const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey: 'wegogolf-auth-storage',
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

// Authentication functions
export const signInWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/oauth2callback',
        scopes: 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/gmail.send',
        queryParams: {
          access_type: 'offline'
        }
      },
    });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};


// export const getGoogleAccessToken = async (userId) => {
//   try {
//     // First check if we have a valid access token
//     const { data: tokenData, error: tokenError } = await supabase
//       .from('user_google_tokens')
//       .select('*')
//       .eq('user_id', userId)
//       .single();

//     if (tokenError) throw tokenError;

//     // If we have a valid access token, return it
//     if (tokenData.access_token && new Date(tokenData.token_expires_at) > new Date()) {
//       return { accessToken: tokenData.access_token, error: null };
//     }

//     // Otherwise, use the refresh token to get a new access token
//     const response = await fetch('https://oauth2.googleapis.com/token', {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/x-www-form-urlencoded',
//       },
//       body: new URLSearchParams({
//         client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
//         client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
//         refresh_token: tokenData.refresh_token,
//         grant_type: 'refresh_token',
//       }),
//     });

//     if (!response.ok) throw new Error('Failed to refresh token');

//     const { access_token, expires_in } = await response.json();

//     // Update the stored tokens
//     const { error: updateError } = await supabase
//       .from('user_google_tokens')
//       .update({
//         access_token,
//         token_expires_at: new Date(Date.now() + expires_in * 1000)
//       })
//       .eq('user_id', userId);

//     if (updateError) throw updateError;

//     return { accessToken: access_token, error: null };
//   } catch (error) {
//     return { accessToken: null, error };
//   }
// };

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { error: null };
  } catch (error) {
    return { error };
  }
};

export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return { user: data.user, error: null };
  } catch (error) {
    return { user: null, error };
  }
};

// User role functions
export const getUserRole = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .single();
    
    // Suppress the error if no rows are returned (means employee by default)
    if (error) {
      if (error.code === 'PGRST116') {
        // No entry found, treat as employee
        return { role: 'employee', error: null };
      }
      throw error;
    }
    return { role: data?.role || 'employee', error: null };
  } catch (error) {
    // Only log unexpected errors
    if (error.code !== 'PGRST116') {
      console.error('Error fetching user role:', error);
    }
    return { role: 'employee', error: null };
  }
};

export const setUserRole = async (userId, role) => {
  try {
    // Upsert: if exists, update; if not, insert
    const { data, error } = await supabase
      .from('user_roles')
      .upsert([{ user_id: userId, role }], { onConflict: ['user_id'] });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// Remove a user's role (makes them employee by default)
export const updateUserRole = async (userId, newRole) => {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .upsert({ 
        user_id: userId, 
        role: newRole 
      }, { 
        onConflict: 'user_id' 
      });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// Time tracking functions
export const saveTimeEntry = async (timeData) => {
  try {
    const { data, error } = await supabase
      .from('time_entries')
      .insert(timeData)
      .select();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getTimeEntries = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const deleteTimeEntry = async (timeEntryId, userId) => {
  try {
    // Delete the time entry
    const { data, error } = await supabase
      .from('time_entries')
      .delete()
      .eq('id', timeEntryId)
      .eq('user_id', userId)
      .select();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getRecentTimeEntries = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(10);
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getPaginatedTimeEntriesWithEvents = async (userId, page = 1, pageSize = 10) => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from('time_entries')
      .select(`
        *,
        event:events(
          id,
          title,
          event_date,
          location
        )
      `, { count: 'exact' })
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(from, to);
    
    if (error) throw error;
    
    return { 
      data: data || [], 
      error: null,
      count: count || 0,
      totalPages: Math.ceil((count || 0) / pageSize)
    };
  } catch (error) {
    return { data: null, error, count: 0, totalPages: 0 };
  }
};

export const getLastSevenDaysTimeEntries = async (userId) => {
  try {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId)
      .gte('end_time', sevenDaysAgo.toISOString()) // Greater than or equal to 7 days ago
      .order('end_time', { ascending: false });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getLastThirtyDaysTimeEntries = async (userId) => {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId)
      .gte('end_time', thirtyDaysAgo.toISOString()) // Greater than or equal to 30 days ago
      .order('end_time', { ascending: false });
    
    if (error) throw error;
    // console.log('data last 30 days', data);
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// Update existing functions to include event data
export const getRecentTimeEntriesWithEvents = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('time_entries')
      .select(`
        *,
        event:events(
          id,
          title,
          event_date,
          location
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(10);
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// Function to get time entries for a specific event
export const getTimeEntriesByEvent = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('time_entries')
      .select(`
        *,
        user:user_id(*)
      `)
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// Fetch all time entries for a user, optionally filtered by date range
export const getTimeEntriesForExport = async (userId, { from, to } = {}) => {
  try {
    const { data, error } = await supabase
      .rpc('get_time_entries_for_export', {
        p_user_id: userId,
        p_from_date: from,
        p_to_date: to
      });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// Media upload functions
export const uploadMedia = async (file, eventId, userId) => {
  try {
    // console.log("uploading media UploadMedia function", file, eventId, userId);
    // Create a unique path using timestamp and original filename
    const timestamp = new Date().toISOString()
      .replace(/[-:]/g, '')   // Remove hyphens and colons
      .replace('T', '_')      // Replace T with underscore
      .replace('Z', '');      // Remove Z
    const sanitizedFileName = sanitizeFilename(file.name);
    const uniqueFileName = `${timestamp}-${sanitizedFileName}`;
    const path = `uploads/${uniqueFileName}`;
    
    const { data: storageData, error: storageError } = await supabase
      .storage
      .from('event-media')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (storageError) {
      // Check for duplicate file error (409)
      if (storageError.statusCode === 409) {
        return { 
          data: null, 
          error: { 
            message: 'A file with this name already exists. Please rename the file and try again.' 
          } 
        };
      }
      throw storageError;
    }

    const { data: dbData, error: dbError } = await supabase
      .from('event_media')
      .insert({
        event_id: eventId,
        media_url: path,
        file_type: file.type,
        original_filename: file.name, // Store original filename
        created_at: new Date().toISOString(),
        created_by: userId
      })
      .select()
      .single();
    
    if (dbError) {
      await supabase.storage.from('event-media').remove([path]);
      throw dbError;
    }
    const { data: publicUrlData, error: publicUrlError } = await supabase
      .storage
      .from('event-media')
      .getPublicUrl(path);
    
    if (publicUrlError) throw publicUrlError;

    return { 
      data: {
        ...dbData,
        original_filename: file.name // Include original filename in response
      }, 
      error: null, 
      publicUrl: publicUrlData.publicUrl 
    };
  } catch (error) {
    console.error('Error in uploadMedia:', error);
    return { data: null, error };
  }
};

export const getEventMedia = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('event_media')
      .select('*')
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;

    const mediaWithUrls = await Promise.all(
      data.map(async (item) => {
        const { data: urlData } = supabase
          .storage
          .from('event-media')
          .getPublicUrl(item.media_url);

        return {
          ...item,
          publicUrl: urlData.publicUrl
        };
      })
    );

    return { data: mediaWithUrls, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const fetchMediaFromStorage = async (mediaItems) => {
  try { 
    
    const validMediaItems = mediaItems.filter(item => 
      item && 
      item.media_url && 
      item.file_type &&
      item.id
    );

    const mediaWithUrls = await Promise.all(
      validMediaItems.map(async (item) => {
        const { data } = supabase
          .storage
          .from('event-media')
          .getPublicUrl(item.media_url);

        return {
          ...item,
          publicUrl: data.publicUrl
        };
      })
    );

    return { data: mediaWithUrls, error: null };
  } catch (error) {
    console.error('Error getting media URLs:', error);
    return { data: null, error };
  }
};

export const deleteMedia = async (mediaId, mediaUrl) => {
  try {
    const { error: storageError } = await supabase
      .storage
      .from('event-media')
      .remove([mediaUrl]);

    if (storageError) throw storageError;

    const { error: dbError } = await supabase
      .from('event_media')
      .delete()
      .eq('id', mediaId);

    if (dbError) throw dbError;

    return { error: null };
  } catch (error) {
    console.error('Error in deleteMedia:', error);
    return { error };
  }
};

export const saveEvent = async (eventData) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .insert(eventData)
      .select()
      .single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const updateEvent = async (eventId, eventData) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .update(eventData)
      .eq('id', eventId)
      .select();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getEvents = async () => {
  try {
    // First, get all events with their event_employees
    const { data: eventsData, error: eventsError } = await supabase
      .from('events')
      .select(`
        *,
        event_employees (*)
      `)
      .order('event_date', { ascending: true });
    
    if (eventsError) throw eventsError;

    // Get all unique user IDs from event_employees AND account_rep_id
    const employeeUserIds = [...new Set(
      eventsData.flatMap(event => 
        event.event_employees.map(emp => emp.user_id)
      )
    )];
    
    // Get all account rep IDs (filter out nulls)
    const accountRepIds = [...new Set(
      eventsData
        .map(event => event.account_rep_id)
        .filter(Boolean) // Remove null/undefined values
    )];
    
    // Combine all user IDs we need to fetch
    const allUserIds = [...new Set([...employeeUserIds, ...accountRepIds])];

    // Get user information using the RPC function for all users we need
    const { data: userData, error: userError } = await supabase
      .rpc('get_users_by_ids_with_name', { user_ids: allUserIds });

    if (userError) throw userError;

    // Create a map of user data by ID for quick lookup
    const userMap = {};
    if (userData) {
      userData.forEach(user => {
        userMap[user.id] = user;
      });
    }

    // Combine the data
    const enrichedEvents = eventsData.map(event => ({
      ...event,
      event_employees: event.event_employees.map(emp => ({
        ...emp,
        user: userMap[emp.user_id] || { id: emp.user_id }
      })),
      // Add account rep data
      account_rep: event.account_rep_id ? userMap[event.account_rep_id] : null
    }));

    return { data: enrichedEvents, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

//Checks for events conflicting events on a day
export const fetchDayEvents = async (date) => {
  try {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const { data: events, error } = await supabase
      .from('events')
      .select('*')
      .gte('start_time', startOfDay.toISOString())
      .lte('start_time', endOfDay.toISOString())
      .order('start_time', { ascending: true });

    if (error) throw error;
    return events;
  } catch (error) {
    console.error('Error fetching day events:', error);
    return [];
  }
};

export const getPaginatedEvents = async (page = 1, pageSize = 10, search = '') => {
  let from;
  let to;
  if (search && search.trim() !== '') {
    from = 0;
    to = 10;
  } else {
    from = (page - 1) * pageSize;
    to = from + pageSize - 1;
  }
  

  let query = supabase
    .from('events')
    .select('*', { count: 'exact' })
    .order('event_date', { ascending: true })
    .range(from, to);

  if (search && search.trim() !== '') {
    const safeSearch = search.trim().replace(/[%_]/g, ''); // Prevent wildcard injection
    query = query.or(`title.ilike.%${safeSearch}%,location.ilike.%${safeSearch}%`);
  }

  const { data, error, count } = await query;
  // console.log('data from getPaginatedEvents', data);
  if (error) throw error;
  return { data, count };
};

export const getUpcomingEvents = async () => {
  try {
    const today = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .gte('event_date', today)
      .order('event_date', { ascending: true });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getEventsByUser = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('event_employees')
      .select(`
        *,
        event:events(*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Extract events and get account rep IDs
    const events = data.map(item => item.event).filter(Boolean);
    const accountRepIds = [...new Set(
      events
        .map(event => event.account_rep_id)
        .filter(Boolean) // Remove null/undefined values
    )];
    
    // If there are account rep IDs to fetch, get the user data
    if (accountRepIds.length > 0) {
      const { data: userData, error: userError } = await supabase
        .rpc('get_users_by_ids_with_name', { user_ids: accountRepIds });
      
      if (!userError && userData) {
        // Create a map for quick lookup
        const userMap = {};
        userData.forEach(user => {
          userMap[user.id] = user;
        });
        
        // Add account rep data to events
        const enrichedData = data.map(item => ({
          ...item,
          event: item.event ? {
            ...item.event,
            account_rep: item.event.account_rep_id ? userMap[item.event.account_rep_id] : null
          } : null
        }));
        
        return { data: enrichedData, error: null };
      } else if (userError) {
        console.error('Error fetching account rep data:', userError);
      }
    }
    
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const deleteEvent = async (eventId, googleEventId) => {
  try {
    // 1. Delete from Google Calendar if googleEventId exists
    if (googleEventId) {
      try {
        const response = await makeGoogleApiRequest(
          `https://www.googleapis.com/calendar/v3/calendars/primary/events/${googleEventId}`,
          {
            method: 'DELETE',
          }
        );
        if (!response.ok && response.status !== 404) {
          // 404 is ok (already deleted)
          throw new Error('Failed to delete Google Calendar event');
        }
      } catch (error) {
        console.warn('Failed to delete Google Calendar event, continuing with database deletion:', error);
      }
    }

    // 2. Delete from Supabase
    const { data: existingEvent, error: fetchError } = await supabase
      .from('events')
      .select('*')
      .eq('id', eventId)
      .single();

    if (fetchError) throw fetchError;
    if (!existingEvent) throw new Error('Event not found');

    const { data, error: eventError } = await supabase
      .from('events')
      .delete()
      .eq('id', eventId)
      .select();

    if (eventError) throw eventError;

    return { error: null };
  } catch (error) {
    console.error('Error in deleteEvent:', error);
    return { error };
  }
};

export const getEventEmployees = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('event_employees')
      .select('*')
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    // console.log('data from getEventEmployees', data);
    
    if (error) throw error;
    
    // If you need user details, you can fetch them separately
    if (data && data.length > 0) {
      // Get all user IDs
      const userIds = data.map(employee => employee.user_id);
      
      // Get user information using your RPC function
      const { data: userData, error: userError } = await supabase
        .rpc('get_users_by_ids_with_name', { user_ids: userIds });
      // console.log('userData from getEventEmployees', userData);
      if (userError) throw userError;
      
      // Create a map of user data by ID for quick lookup
      const userMap = {};
      if (userData) {
        userData.forEach(user => {
          userMap[user.id] = user;
        });
      }
      
      // Combine employee records with user data
      const employeesWithUserData = data.map(employee => ({
        ...employee,
        user: userMap[employee.user_id] || { id: employee.user_id }
      }));
      
      return { data: employeesWithUserData, error: null };
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error getting event employees:', error);
    return { data: null, error };
  }
};

export const getEventDetails = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('id', eventId)
      .single();
    
    if (error) throw error;
    
    // If there's an account_rep_id, fetch the user data
    if (data.account_rep_id) {
      const { data: userData, error: userError } = await supabase
        .rpc('get_users_by_ids_with_name', { user_ids: [data.account_rep_id] });
      
      if (userError) {
        console.error('Error fetching account rep:', userError);
        // Don't throw error, just continue without account rep data
      } else if (userData && userData.length > 0) {
        // Add the account rep data to the event
        data.account_rep = userData[0];
      }
    }
    
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getEventAssignments = async (eventId) => {
  try {
    // Step 1: Get all assignments for this event
    const { data: assignments, error: assignmentsError } = await supabase
      .from('event_employees')
      .select('*')
      .eq('event_id', eventId);
    
    if (assignmentsError) throw assignmentsError;
    
    if (!assignments || assignments.length === 0) {
      return { data: [], error: null };
    }
    
    // Step 2: Get all users using your existing function
    const { data: users, error: usersError } = await supabase
      .rpc('get_users_with_name');
    
    if (usersError) throw usersError;
    // Step 3: Create a map of user_id to user data for quick lookup
    const userMap = {};
    users.forEach(user => {
      userMap[user.id] = user;
    });
    // Step 4: Combine the assignments with user data
    const enrichedAssignments = assignments.map(assignment => ({
      ...assignment,
      user: userMap[assignment.user_id] || { id: assignment.user_id, email: null }
    }));
    return { data: enrichedAssignments, error: null };
  } catch (error) {
    console.error('Error in getEventAssignments:', error);
    return { data: null, error };
  }
};

export const assignUsersToEvent = async (eventId, users) => {
  try {
    const rows = users.map(user => (
      {
      event_id: eventId,
      user_id: user.value,
      created_at: new Date().toISOString()
    }));

    const { data, error } = await supabase
      .from('event_employees')
      .insert(rows)
      .select();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const removeUserFromEvent = async (assignmentId) => {
  try {
    const { data, error } = await supabase
      .from('event_employees')
      .delete()
      .eq('id', assignmentId)
      .select();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const updateEventDescription = async (eventId, description) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .update({ description, updated_at: new Date().toISOString() })
      .eq('id', eventId)
      .select();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const updateEventLocation = async (eventId, locationData) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .update({ 
        location: locationData.address,
        location_lat: locationData.lat,
        location_lng: locationData.lng,
        location_place_id: locationData.placeId,
        timezone: locationData.timezone,
        updated_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .select();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const updateEventDateTime = async (eventId, { startTime, endTime, eventDate }) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .update({ 
        start_time: startTime,
        end_time: endTime,
        event_date: eventDate,
        updated_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .select();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const addEventContacts = async (contacts) => {
  try {
    const { data, error } = await supabase
      .from('event_contacts')
      .insert(contacts)
      .select();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// READ: Get all contacts for an event
export const getEventContacts = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('event_contacts')
      .select('*')
      .eq('event_id', eventId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// UPDATE: Update a contact
export const updateEventContact = async (contactId, updatedFields) => {
  try {
    const { data, error } = await supabase
      .from('event_contacts')
      .update(updatedFields)
      .eq('id', contactId)
      .select()
      .single();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// DELETE: Delete a contact
export const deleteEventContact = async (contactId) => {
  try {
    const { error } = await supabase
      .from('event_contacts')
      .delete()
      .eq('id', contactId);
    if (error) throw error;
    return { error: null };
  } catch (error) {
    return { error };
  }
};

export const getEventRating = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('event_ratings')
      .select('*')
      .eq('event_id', eventId)
      .single();
    
    // Handle both PGRST116 (no rows) and 406 (Not Acceptable) errors
    if (error) {
      if (error.code === 'PGRST116' || error.status === 406) {
        // Return null data for both cases
        return { data: null, error: null };
      }
      throw error;
    }
    return { data, error: null };
  } catch (error) {
    // Only log unexpected errors
    if (error.code !== 'PGRST116' && error.status !== 406) {
      console.error('Error fetching event rating:', error);
    }
    return { data: null, error: null };
  }
};

// Submit a rating for an event
export const submitEventRating = async (eventId, rating, userId) => {
  try {
    // First check if a rating already exists
    const { data: existingRating } = await getEventRating(eventId);
    
    if (existingRating) {
      return { 
        data: null, 
        error: { message: 'This event has already been rated' } 
      };
    }

    const { data, error } = await supabase
      .from('event_ratings')
      .insert({
        event_id: eventId,
        rating: rating,
        created_by: userId,
      })
      .select()
      .single();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// READ: Get all equipment
export const getEquipment = async () => {
  try {
    const { data, error } = await supabase
      .from('equipment')
      .select('id,name')
      .order('name', { ascending: true });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// CREATE: Add new equipment
export const createEquipment = async (equipmentName) => {
  try {
    const { data, error } = await supabase
      .from('equipment')
      .insert([{ name: equipmentName }])
      .select();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// UPDATE: Update existing equipment
export const updateEquipment = async (equipmentId, equipmentData) => {
  try {
    const { data, error } = await supabase
      .from('equipment')
      .update(equipmentData)
      .eq('id', equipmentId)
      .select();
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// DELETE: Delete equipment
export const deleteEquipment = async (equipmentId) => {
  try {
    const { error } = await supabase
      .from('equipment')
      .delete()
      .eq('id', equipmentId);
    if (error) throw error;
    return { error: null };
  } catch (error) {
    return { error };
  }
};

// READ: Get equipment for an event
export const getEventEquipment = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('event_equipment')
      .select('*, equipment(*)')
      .eq('event_id', eventId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return { data, error: null };
  } catch (error) { 
    return { data: null, error };
  }
};

// WRITE: Add equipment to an event 
export const addEquipmentToEvent = async (eventId, equipmentList) => {
  try {
    const rows = equipmentList.map(eq => ({
      event_id: eventId,
      equipment_id: eq.value,      // or eq.value, depending on your data
      quantity: eq.quantity > 0 ? eq.quantity : 1,    // if you have a quantity field
      created_at: new Date().toISOString()
    }));

    const { data, error } = await supabase
      .from('event_equipment')
      .insert(rows)
      .select();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// Update event equipment quantity
export const updateEventEquipment = async (eventEquipmentId, quantity) => {
  const { error } = await supabase
    .from('event_equipment')
    .update({ quantity:quantity })
    .eq('id', eventEquipmentId);
  if (error && error.code !== 'PGRST116' && error.status !== 406) throw error;
  return { error: null };
};

// Delete event equipment
export const deleteEventEquipment = async (eventEquipmentId) => {
  const { error } = await supabase
    .from('event_equipment')
    .delete()
    .eq('id', eventEquipmentId);
  if (error) throw error;
  return { error: null };
};

//Event Issues
export const createEventIssue = async (issueData) => {
  try {
    const { data, error } = await supabase
      .from('event_issues')
      .insert({
        event_id: issueData.eventId,
        issue_type: issueData.issueType,
        description: issueData.description,
        created_by: issueData.userId,
        created_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error creating event issue:', error);
    return { data: null, error };
  }
};

export const getEventIssues = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('event_issues')
      .select('*')
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching event issues:', error);
    return { data: null, error };
  }
};

//Event Status
export const updateEventStatus = async (eventId, newStatus) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .update({ event_status: newStatus })
      .eq('id', eventId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error updating event status:', error);
    return { data: null, error };
  }
};


// Google Calendar Description Management Functions
export const buildGoogleEventDescription = (equipment = [], contacts = [], notes = '') => {
  const equipmentList = equipment.length > 0 
    ? equipment.map(eq => `• ${eq.equipment.name} (${eq.quantity || '1'})`).join('\n')
    : 'None';
    
  const contactsList = contacts.length > 0
    ? contacts.map(contact => 
        `  • ${contact.name}${contact.role ? ` (${contact.role})` : ''}` +
        `${contact.email ? `\n    Email: ${contact.email}` : ''}` +
        `${contact.phone ? `\n    Phone: ${contact.phone}` : ''}`
      ).join('\n')
    : 'None';

  return `<b>Equipment:</b>
${equipmentList}

<b>Contacts:</b>
${contactsList}

<b>Event Notes:</b>
${notes || 'None'}`;
};

export const parseExistingDescription = (description) => {
  
  if (!description) {
    return { equipment: '', contacts: '', notes: '' };
  }

  const sections = {
    equipment: '',
    contacts: '',
    notes: ''
  };

  // Look for each section and extract content, accounting for HTML tags
  const equipmentMatch = description.match(/<b>Equipment:<\/b>\s*([\s\S]*?)(?=\n\n<b>(?:Contacts:|Event Notes:)|$)/i);
  const contactsMatch = description.match(/<b>Contacts:<\/b>\s*([\s\S]*?)(?=\n\n<b>(?:Equipment:|Event Notes:)|$)/i);
  const notesMatch = description.match(/<b>Event Notes:<\/b>\s*([\s\S]*?)(?=\n\n<b>(?:Equipment:|Contacts:)|$)/i);

  if (equipmentMatch) {
    sections.equipment = equipmentMatch[1].trim();
  }
  if (contactsMatch) {
    sections.contacts = contactsMatch[1].trim();
  }
  if (notesMatch) {
    // Clean up any stray HTML tags from the notes content
    sections.notes = notesMatch[1].trim().replace(/<\/?b>/g, '');
  }

  // If no structured sections found, treat entire description as notes
  if (!equipmentMatch && !contactsMatch && !notesMatch) {
    sections.notes = description.trim().replace(/<\/?b>/g, '');
  }

  return sections;
};

export const updateGoogleEventSection = async (googleEventId, sectionType, newContent) => {
  if (!googleEventId) {
    return { error: 'No Google event ID provided' };
  }

  try {
    // Get current event using the new token management
    const getResponse = await makeGoogleApiRequest(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${googleEventId}`
    );
    
    if (!getResponse.ok) {
      throw new Error('Failed to fetch current Google Calendar event');
    }
    
    const currentEvent = await getResponse.json();
    const currentDescription = currentEvent.description || '';
    
    // Parse existing description
    const parsed = parseExistingDescription(currentDescription);
    
    // Build new content for the specific section
    let newSectionContent;
    switch (sectionType) {
      case 'equipment':
        newSectionContent = Array.isArray(newContent) && newContent.length > 0
          ? newContent.map(eq => `• ${eq.equipment.name} (${eq.quantity || '1'})`).join('\n')
          : '';
        break;
        
      case 'contacts':
        newSectionContent = Array.isArray(newContent) && newContent.length > 0
          ? newContent.map(contact => 
              `• ${contact.name}${contact.role ? ` (${contact.role})` : ''}` +
              `${contact.email ? `\n  Email: ${contact.email}` : ''}` +
              `${contact.phone ? `\n  Phone: ${contact.phone}` : ''}`
            ).join('\n')
          : '';
        break;
        
      case 'notes':
        newSectionContent = newContent || '';
        break;
        
      default:
        throw new Error('Invalid section type');
    }

    // Build complete updated description - only include sections that have content
    const updatedSections = { ...parsed };
    updatedSections[sectionType] = newSectionContent;
    
    const descriptionParts = [];

    // Only add sections if they have meaningful content (not "None")
    if (updatedSections.equipment && updatedSections.equipment !== 'None' && updatedSections.equipment.trim()) {
      descriptionParts.push(`<b>Equipment:</b>\n${updatedSections.equipment}`);
    }

    if (updatedSections.contacts && updatedSections.contacts !== 'None' && updatedSections.contacts.trim()) {
      descriptionParts.push(`<b>Contacts:</b>\n${updatedSections.contacts.trim()}`);
    }

    if (updatedSections.notes && updatedSections.notes !== 'None' && updatedSections.notes.trim()) {
      descriptionParts.push(`<b>Event Notes:</b>\n${updatedSections.notes}`);
    }

    const updatedDescription = descriptionParts.join('\n\n');

    // Update the event using the new token management
    const response = await makeGoogleApiRequest(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${googleEventId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ description: updatedDescription }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to update Google Calendar event ${sectionType}`);
    }
    
    return { data: updatedDescription, error: null };
  } catch (error) {
    console.error(`Error updating Google Calendar event ${sectionType}:`, error);
    return { data: null, error: error.message };
  }
};

// Convenience functions for specific sections
export const updateGoogleEventEquipment = async (googleEventId, equipment) => {
  return updateGoogleEventSection(googleEventId, 'equipment', equipment);
};

export const updateGoogleEventContacts = async (googleEventId, contacts) => {
  return updateGoogleEventSection(googleEventId, 'contacts', contacts);
};

export const updateGoogleEventNotes = async (googleEventId, notes) => {
  return updateGoogleEventSection(googleEventId, 'notes', notes);
};

// Google Token Management Functions
export const getValidGoogleAccessToken = async (userId) => {
  try {
    // First check if we have a valid access token in the database
    const { data: tokenData, error: tokenError } = await supabase
      .from('user_google_tokens')
      .select('access_token, token_expires_at, refresh_token')
      .eq('user_id', userId)
      .single();

    if (tokenError) {
      console.error('Error fetching token data:', tokenError);
      return { accessToken: null, error: tokenError };
    }

    // If we have a valid access token that hasn't expired, return it
    if (tokenData.access_token && new Date(tokenData.token_expires_at) > new Date()) {
      // Update localStorage with the current valid token
      localStorage.setItem('googleAccessToken', tokenData.access_token);
      return { accessToken: tokenData.access_token, error: null };
    }

    // Otherwise, use the refresh token to get a new access token
    console.log('Access token expired, refreshing...');
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
        refresh_token: tokenData.refresh_token,
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Failed to refresh token:', errorData);
      throw new Error('Failed to refresh token: ' + (errorData.error_description || errorData.error));
    }

    const { access_token, expires_in } = await response.json();

    // Calculate expiration time (expires_in is in seconds)
    const expiresAt = new Date(Date.now() + (expires_in * 1000));

    // Update the stored tokens
    const { error: updateError } = await supabase
      .from('user_google_tokens')
      .update({
        access_token,
        token_expires_at: expiresAt.toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (updateError) {
      console.error('Error updating token:', updateError);
      throw updateError;
    }

    // Update localStorage with the new token
    localStorage.setItem('googleAccessToken', access_token);
    console.log('Successfully refreshed Google access token');

    return { accessToken: access_token, error: null };
  } catch (error) {
    console.error('Error in getValidGoogleAccessToken:', error);
    return { accessToken: null, error };
  }
};

// Helper function to handle Google API requests with automatic token refresh
export const makeGoogleApiRequest = async (url, options = {}) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get a valid access token (refreshes if needed)
  const { accessToken, error: tokenError } = await getValidGoogleAccessToken(user.id);
  
  if (tokenError || !accessToken) {
    throw new Error('Unable to get valid Google access token');
  }

  // Make the API request with the valid token
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  // If we get a 401, the token might have expired between our check and the request
  if (response.status === 401) {
    console.log('Got 401, attempting token refresh...');
    
    // Try to refresh the token one more time
    const { accessToken: newToken, error: refreshError } = await getValidGoogleAccessToken(user.id);
    
    if (refreshError || !newToken) {
      throw new Error('Unable to refresh Google access token');
    }

    // Retry the request with the new token
    const retryResponse = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${newToken}`,
        'Content-Type': 'application/json',
      },
    });

    return retryResponse;
  }

  return response;
};

export const searchUserEvents = async (userId, searchTerm) => {
  try {
    const { data, error } = await supabase
      .rpc('search_user_events', {
        p_user_id: userId,
        p_search_term: searchTerm,
        p_limit: 15
      });
    
    if (error) throw error;
    
    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error };
  }
}; 

export { supabase }; 

export const updateEventVenueType = async (eventId, venueType) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .update({ venue_type: venueType })
      .eq('id', eventId)
      .select()
      .single();
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}; 